/**
 * Parses Shopify cart validation errors (e.g. 422 from /cart/add.js).
 *
 * Shopify wraps per-product limit errors as a JSON-encoded array inside
 * the outer `description` field:
 *   { status: 422, message: "[...]", description: "[{id, title, error, cartLevelError, ...}]" }
 *
 * Returns a human-readable message by joining the cartLevelError (preferred)
 * or error field from each failing item, falling back to the outer description/message,
 * and ultimately to the raw error message.
 */
export function parseShopifyCartError(error: any): string {
  const body = error?.body ?? error?.message;

  try {
    const outer = JSON.parse(body);
    const raw = outer?.description || outer?.message;

    try {
      const items = JSON.parse(raw);
      if (Array.isArray(items)) {
        const messages = items
          .map((item: any) => item.cartLevelError || item.error)
          .filter(Boolean);
        if (messages.length > 0) return messages.join(' ');
      }
    } catch {
      if (typeof raw === 'string' && raw.length > 0) return raw;
    }

    if (typeof outer?.description === 'string' && outer.description.length > 0) {
      return outer.description;
    }
    if (typeof outer?.message === 'string' && outer.message.length > 0) {
      return outer.message;
    }
  } catch {
    // body wasn't JSON
  }

  return error?.message || 'Unable to add product to cart.';
}

export async function dispatchCartEvent(
  cartData: any,
  sourceId: string = 'single-storefront',
  sections?: Record<string, string>
): Promise<void> {
  try {
    const doc = typeof window !== 'undefined' ? window.document : null;
    if (!doc) return;

    const eventData = {
      resource: cartData,
      sourceId,
      data: {
        source: 'single-storefront',
        itemCount: cartData?.item_count || 0,
        cartData,
      },
    };

    doc.dispatchEvent(new CustomEvent('cart:update', {
      detail: eventData,
      bubbles: true,
      cancelable: true,
    }));

    dispatchHorizonCartLinesUpdate(cartData, sourceId, sections);

    if (typeof window.publish === 'function') {
      await window.publish('cart-update', {
        source: 'single-storefront',
        cartData,
      });
    }
  } catch (err) {
    console.error('[dispatchCartEvent]', err);
  }
}

/**
 * Horizon 4.0+ moved its cart components (cart-icon, cart-drawer, product-form)
 * off the legacy `cart:update` CustomEvent onto @shopify/events standard events.
 * They listen for `shopify:cart:lines-update` and read `event.promise`, a promise
 * resolving to `{ cart, detail }` — `cart.totalQuantity` drives the icon bubble
 * (falling back to `detail.itemCount`) and `detail.sections` feeds re-renders.
 * Dispatch a structurally compatible event alongside the legacy ones; themes
 * that don't listen for it simply ignore it.
 */
function dispatchHorizonCartLinesUpdate(
  cartData: any,
  sourceId: string,
  sections?: Record<string, string>
): void {
  try {
    const currencyCode = cartData?.currency ?? '';
    const toAmount = (cents: number | undefined) => ({
      amount: (Number(cents ?? 0) / 100).toFixed(2),
      currencyCode,
    });

    const cart = {
      id: cartData?.token ?? '',
      totalQuantity: cartData?.item_count ?? 0,
      cost: { totalAmount: toAmount(cartData?.total_price) },
      lines: (cartData?.items ?? []).map((item: any) => ({
        id: item.key,
        quantity: item.quantity,
        cost: { totalAmount: toAmount(item.final_line_price ?? item.line_price) },
      })),
      discountCodes: [],
    };

    const lastItem = cartData?.items?.[cartData.items.length - 1];
    const event = new CustomEvent('shopify:cart:lines-update', {
      bubbles: true,
      cancelable: true,
    });
    // Payload properties live on the event object itself (not detail),
    // matching @shopify/events' CartLinesUpdateEvent.
    Object.assign(event, {
      action: 'add',
      context: 'product',
      lines: lastItem
        ? [
            {
              merchandiseId: `gid://shopify/ProductVariant/${lastItem.variant_id}`,
              quantity: lastItem.quantity,
            },
          ]
        : [],
      promise: Promise.resolve({
        cart,
        detail: {
          items: cartData?.items ?? [],
          source: 'single-storefront',
          sourceId,
          itemCount: cartData?.item_count ?? 0,
          sections,
          didError: false,
        },
      }),
    });

    document.dispatchEvent(event);
  } catch (err) {
    console.error('[dispatchHorizonCartLinesUpdate]', err);
  }
}
